import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import pluralize from 'pluralize';

import {
  formatPhoneNumber,
  formatPrice,
} from 'common/formatters';

import {
  HOUSEHOLD_APPLICATION_STATUS_STARTED,
} from 'common/householdConstants';

import { isApplicationPastStatus } from 'common/householdApplication/applicationHelpers';
import { getHouseholdPets } from 'common/householdHelpers';
import { PET_OPTION_VALUE_DOG } from 'common/petOptionItems';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrCardToggle from 'client/components/SrCardToggle/SrCardToggle';

import SrTag, {
  SR_TAG_THEME_GREEN_ADOBE,
  SR_TAG_THEME_WHITE_BLACK,
  SR_TAG_THEME_WHITE_GREEN,
} from 'client/components/SrTag/SrTag';

const SrPrequalificationCard = ({ prequalification, qualifications }) => {

  const SrPrequalificationRequirement = ({ isQualified, children }) => {
    return (
      <SrFlex className='SrPrequalificationRequirement' align='center'>
        <SrBox marginBottom='auto'>
          <div
            className={classNames('icon', {
              isQualified: isQualified,
            })}
          />
        </SrBox>
        <SrBox>
          {children}
        </SrBox>
      </SrFlex>
    );
  };

  const _getTag = () => {
    const application = prequalification.application || {};
    if (!application.status || !qualifications.isQualified || !prequalification.isLatest) {
      return {
        theme: SR_TAG_THEME_WHITE_BLACK,
        label: 'No application started',
      };
    }
    if (application.status === HOUSEHOLD_APPLICATION_STATUS_STARTED) {
      return {
        theme: SR_TAG_THEME_WHITE_GREEN,
        label: 'Application started',
      };
    }
    return {
      theme: SR_TAG_THEME_GREEN_ADOBE,
      label: 'Application completed',
    };
  };

  const isApplicationPastStarted = () => {
    if (!prequalification.application?.status) {
      return false;
    }
    return isApplicationPastStatus(prequalification.application, HOUSEHOLD_APPLICATION_STATUS_STARTED);
  };

  const handleTagClick = () => {
    if (!prequalification.application) {
      return null;
    }

    const { householdId } = prequalification.application;
    const shouldScroll = _shouldScroll();

    if (shouldScroll) {
      const [applicationElement] = global.document.getElementsByClassName(`pendingApprovalId-${householdId}`);
      const topOffset = 75;

      if (applicationElement) {
        const elementPosition = applicationElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + global.window.pageYOffset - topOffset;

        global.window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });

        applicationElement.classList.add('yellow-border');
        setTimeout(() => {
          applicationElement.classList.remove('yellow-border');
        }, 3000);
      }
    }
  };

  const _getDogs = () => {
    return prequalification.pets.filter((pet) => pet.type === PET_OPTION_VALUE_DOG);
  };

  const _shouldScroll = () => {
    return isApplicationPastStarted()
      && prequalification.isLatest
      && qualifications.isQualified;
  };

  const { petsByType } = getHouseholdPets({
    householdApplications: [{
      pets: prequalification.pets,
    }],
  });

  const prequalDate = moment(prequalification.createdDate).format('MMMM D');
  const dogs = _getDogs();
  const tag = _getTag();
  const isScrollable = _shouldScroll();

  const {
    rentalRate: rentalRateQualification,
    moveInDate: moveInDateQualification,
    pets: petsQualification,
    petBreeds: petBreedsQualification,
    evictions: evictionsQualification,
    bankruptcies: bankruptciesQualification,
    creditScore: creditScoreQualification,
    monthlyIncome: monthlyIncomeQualification,
    guarantor: guarantorQualification,
    section8: section8Qualification,
  } = qualifications.requirements;

  const titleDOM = (
    <SrFlex
      direction='row'
      align='baseline'
      marginTop='12px'
      marginBottom='5px'
    >
      <SrText
        weight='800'
        color={
          qualifications.isQualified
            ? 'green'
            : 'orange300'
        }
        display='block'
        font='greycliff'
        fontSize='26px'
        lineHeight='25px'
      >
        {qualifications.matches}/{qualifications.totalQualifications}
      </SrText>
      <SrBox
        marginLeft='3px'
      >
        <SrText
          weight='800'
          color={
            qualifications.isQualified
              ? 'green'
              : 'orange300'
          }
          display='block'
          font='greycliff'
          fontSize='16px'
          lineHeight='25px'
        >
          Qualifications Met
        </SrText>
      </SrBox>
    </SrFlex>
  );

  return (
    <div className='SrPrequalificationCard'>
      <SrBox
        className='SrPartnerHouseholdPendingApprovalCardHeader'
      >
        <SrCardToggle
          titleComponent={titleDOM}
          noBorderTop
        >
          <SrBox
            paddingLeft='20px'
          >
            <SrText
              font='greycliff'
              weight='800'
              color='blueGreycliff'
              fontSize='16px'
            >
              {`Prequalified on ${prequalDate}`}
            </SrText>
            <SrBox className='qualificationList'>
              <SrPrequalificationRequirement
                isQualified={rentalRateQualification.isQualified}
              >
                <SrText
                  color='green'
                  font='greycliff'
                  weight='800'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  Rental Rate:&nbsp;
                </SrText>
                <SrText
                  color='green'
                  font='greycliff'
                  weight='800'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  {rentalRateQualification.value}
                </SrText>
              </SrPrequalificationRequirement>
              <SrPrequalificationRequirement
                isQualified={moveInDateQualification.isQualified}
              >
                <SrText
                  color={moveInDateQualification.isQualified ? 'green' : 'red'}
                  font='greycliff'
                  weight='800'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  Move-in Date:&nbsp;
                </SrText>
                <SrText
                  color={moveInDateQualification.isQualified ? 'green' : 'red'}
                  font='greycliff'
                  weight='800'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  {
                    `${moment(prequalification.movePeriodStartDate).format('MMM D')} - ` +
                    `${moment(prequalification.movePeriodEndDate).format('MMM D')}`
                  }
              &nbsp;
                </SrText>
              </SrPrequalificationRequirement>
              <SrPrequalificationRequirement
                isQualified={petsQualification.isQualified}
              >
                <SrText
                  color={petsQualification.isQualified ? 'green' : 'red'}
                  font='greycliff'
                  weight='800'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  {`${pluralize('Pet', prequalification.pets.length)}: `}
                  {Object.keys(petsByType).map((petType, petTypeIndex) => {
                    const pets = petsByType[petType];
                    let dogDetailsDOM = null;
                    if (petType.includes(PET_OPTION_VALUE_DOG)) {
                      dogDetailsDOM = (
                        <SrText
                          color={petsQualification.isQualified ? 'green' : 'red'}
                          font='greycliff'
                          weight='800'
                          fontSize='16px'
                          lineHeight='19px'
                        >
                          {' ('}
                          {pets.map((pet, index) => {
                            return (
                              <SrText
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                color={petsQualification.isQualified
                                  ? 'green'
                                  : 'red'}
                                font='greycliff'
                                weight='800'
                                fontSize='16px'
                                lineHeight='19px'
                              >
                                {pet.weight} lbs
                                {(index + 1) < pets.length ? ', ' : ''}
                              </SrText>
                            );
                          })}
                          {')'}
                        </SrText>
                      );
                    }

                    return (
                      <SrText
                        key={petType}
                        color={
                          petsQualification.isQualified
                            ? 'green'
                            : 'red'
                        }
                        font='greycliff'
                        weight='800'
                        fontSize='16px'
                        lineHeight='19px'
                      >
                        {`${pets.length} ${pluralize(petType, pets.length)}`}
                        {dogDetailsDOM}
                        {(petTypeIndex + 1) < Object.keys(petsByType).length ? ', ' : ''}
                      </SrText>
                    );
                  })}
                  {prequalification.pets.length === 0 && (
                    <SrText
                      color='green'
                      font='greycliff'
                      weight='800'
                      fontSize='16px'
                      lineHeight='19px'
                    >
                      No pets
                    </SrText>
                  )}
                </SrText>
              </SrPrequalificationRequirement>
              <SrPrequalificationRequirement
                isQualified={petBreedsQualification.isQualified}
              >
                <SrText
                  color={petBreedsQualification.isQualified ? 'green' : 'red'}
                  font='greycliff'
                  weight='800'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  Dog Breed:&nbsp;
                </SrText>
                {
                  dogs.length > 0
                    ? dogs.map((dog, index) => {
                      return (
                        <SrText
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          color={petBreedsQualification.isQualified ? 'green' : 'red'}
                          font='greycliff'
                          weight='800'
                          fontSize='16px'
                          lineHeight='19px'
                        >
                          {dog.breed}
                          {(index + 1) < dogs.length ? ', ' : ''}
                        </SrText>
                      );
                    })
                    : (
                      <SrText
                        // eslint-disable-next-line react/no-array-index-key
                        color={petBreedsQualification.isQualified ? 'green' : 'red'}
                        font='greycliff'
                        weight='800'
                        fontSize='16px'
                        lineHeight='19px'
                      >
                        N/A
                      </SrText>
                    )
                }
              </SrPrequalificationRequirement>
              <SrPrequalificationRequirement
                isQualified={evictionsQualification.isQualified}
              >
                <SrText
                  color={evictionsQualification.isQualified ? 'green' : 'red'}
                  font='greycliff'
                  weight='800'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  {evictionsQualification.isQualified ? 'No evictions ' : 'Eviction '}
                  {(prequalification.evictionsAccepted
                      && prequalification.evictionsDescription) || ''}
                </SrText>
              </SrPrequalificationRequirement>
              {bankruptciesQualification && (
                <SrPrequalificationRequirement
                  isQualified={bankruptciesQualification?.isQualified}
                >
                  <SrText
                    color={bankruptciesQualification?.isQualified ? 'green' : 'red'}
                    font='greycliff'
                    weight='800'
                    fontSize='16px'
                    lineHeight='19px'
                  >
                    {bankruptciesQualification?.isQualified ? 'No bankruptcies ' : 'Bankruptcy '}
                    {(prequalification.bankruptciesAccepted
                 && prequalification.bankruptciesDescription) || ''}
                  </SrText>
                </SrPrequalificationRequirement>
              )}
              <SrPrequalificationRequirement
                isQualified={creditScoreQualification.isQualified}
              >
                <SrText
                  color={creditScoreQualification.isQualified ? 'green' : 'red'}
                  font='greycliff'
                  weight='800'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  Credit score: {prequalification.prequalCreditScore}
                </SrText>
              </SrPrequalificationRequirement>
              <SrPrequalificationRequirement
                isQualified={monthlyIncomeQualification.isQualified}
              >
                <SrText
                  weight='800'
                  color={monthlyIncomeQualification.isQualified ? 'green' : 'red'}
                  font='greycliff'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  Monthly Income:&nbsp;
                </SrText>
                <SrText
                  color={monthlyIncomeQualification.isQualified ? 'green' : 'red'}
                  weight='800'
                  font='greycliff'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  {
                    `${formatPrice(monthlyIncomeQualification.value)} ` +
                    `(${(monthlyIncomeQualification.value / prequalification.price).toFixed(1)}x Rent)`
                  }
                </SrText>
              </SrPrequalificationRequirement>
              <SrPrequalificationRequirement
                isQualified={guarantorQualification.isQualified}
              >
                <SrText
                  weight='800'
                  color={guarantorQualification.isQualified ? 'green' : 'red'}
                  font='greycliff'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  Guarantor:&nbsp;
                </SrText>
                <SrText
                  color={guarantorQualification.isQualified ? 'green' : 'red'}
                  weight='800'
                  font='greycliff'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  {
                    guarantorQualification.value
                  }
                </SrText>
              </SrPrequalificationRequirement>
              <SrPrequalificationRequirement
                isQualified={section8Qualification.isQualified}
              >
                <SrText
                  weight='800'
                  color={section8Qualification.isQualified ? 'green' : 'red'}
                  font='greycliff'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  Section 8:&nbsp;
                </SrText>
                <SrText
                  color={section8Qualification.isQualified ? 'green' : 'red'}
                  weight='800'
                  font='greycliff'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  {
                    prequalification.receiveHousingVouchers
                      ? 'Need Section 8'
                      : 'No Section 8'
                  }
                </SrText>
              </SrPrequalificationRequirement>
            </SrBox>
          </SrBox>
        </SrCardToggle>
        <SrCardToggle
          title='Applicant:'
          label={prequalification.userName}
          noBorderTop
        >
          <SrBox
            padding='0 20px'
            marginBottom='12px'
          >
            <SrText
              weight='600'
              fontSize='16px'
              font='greycliff'
            >
              {prequalification.userName}
            </SrText>
            {(prequalification.userPhone || prequalification.userEmail) && (
              <SrFlex marginTop='5px' direction='col'>
                {prequalification.userPhone && (
                  <SrText
                    font='greycliff'
                    lineHeight='17px'
                    fontSize='14px'
                    color='grey'
                    weight='600'
                  >
                    {formatPhoneNumber(prequalification.userPhone)}
                  </SrText>
                )}
                {prequalification.userEmail && (
                  <SrText
                    font='greycliff'
                    lineHeight='17px'
                    fontSize='14px'
                    color='grey'
                    weight='600'
                    paddingTop='2px'
                  >
                    {prequalification.userEmail}
                  </SrText>
                )}
              </SrFlex>
            )}
          </SrBox>
        </SrCardToggle>
      </SrBox>
      <SrTag
        onClick={handleTagClick}
        theme={tag.theme}
        className={classNames('prequalTag', {
          isPrequalComplete: isScrollable,
        })}
      >
        {tag.label}
      </SrTag>
    </div>
  );
};

SrPrequalificationCard.displayName = 'SrPrequalificationCard';

SrPrequalificationCard.propTypes = {
  prequalification: PropTypes.object.isRequired,
  qualifications: PropTypes.object.isRequired,
};

export default SrPrequalificationCard;
